import {useEffect, useMemo, useState} from 'react';
import { mapProductIdToTag } from '@/components/utils/utils';
import { useGetFeatureKeys, useGetSubscriptionStatusQuery } from '@viome/viome-base/src/usecase/Misc/MiscQuery';
import { KEYS, LocalStorage } from '@utils/LocalStorage.ts';
import { KitType } from '@viome/viome-base/src/usecase/Bootup/BootupModel';
import { isRecentlyPublished } from '@/components/utils/Timezone';
import { useDynamicConfig } from '@statsig/react-bindings';

const useStorifyConfig = (isSingleStory = false) => {
  const config = useDynamicConfig('public_config');
  const isInternalTesting = config.get('storify_internal_testing', false);
  const localStorageUser = LocalStorage.getItem(KEYS.BOOTUP);
  const { kitType, rnrLastGeneratedDate  } = localStorageUser && JSON.parse(localStorageUser);
  const isResultsReleasedRecently = isRecentlyPublished(
    rnrLastGeneratedDate,
  );
  const {
    data,
    isLoading: isStoriesLoading,
    isSuccess,
  } = useGetSubscriptionStatusQuery();
  const { data: featureKeys } = useGetFeatureKeys();
  const [tags, setTags] = useState<string[]>([]);

  const isStorifyEnabled = featureKeys?.storify_enabled || false;
  const storifyWidgetId = featureKeys?.storify_widget_id || "8612";
  const isPHPRO = kitType === KitType.PHPRO;

  const subscriptionData = useMemo(() => {
    if (!isStorifyEnabled) {
      return;
    }
    const products = data?.products ?? [];

    const subscribedProducts = products
      .filter((item) => item?.is_subscribed)
      .map((element) => mapProductIdToTag(element.id));

    //tag has NOT-SUBSCRIBED, SUBSCRIBED,CANCELED-SUBSCRIBED
    const otherTags = data?.subscription_details?.tag ?? '';

    //CONCIERGE, STANDARD
    const subscriptionType = data?.subscription_details?.type ?? '';

    return { otherTags, subscriptionType, subscribedProducts };
  }, [data, isStorifyEnabled]);

  useEffect(() => {
    if (!isStorifyEnabled) {
      return;
    }
    if (isSingleStory) {
      if (kitType) {
        setTags([`${kitType}-RR`]);
      } else {
        setTags(['RECOMMENDATION-ONBOARDING-FIRST-WEEK']);
      }
    } else {
      setTags((prevTags) => {
        const newTags = [...prevTags, isPHPRO ? KitType.FBI : kitType];
        if (isResultsReleasedRecently) {
          newTags.push('FIRST-WEEK-STORY');
        }
        if (
          isSuccess &&
          subscriptionData?.subscribedProducts &&
          subscriptionData?.subscribedProducts.length > 0
        ) {
          newTags.push(...subscriptionData.subscribedProducts);
        }
        if (subscriptionData?.otherTags) {
          newTags.push(subscriptionData.otherTags);
        }
        if (subscriptionData?.subscriptionType) {
          newTags.push(subscriptionData.subscriptionType);
        }
        if (isInternalTesting) {
          newTags.push('INTERNAL-TESTING');
        }
        newTags.push('ALL-USER');
        return [...new Set(newTags.filter(Boolean))];
      });
    }
  }, [isSuccess, subscriptionData, kitType, isStorifyEnabled, isPHPRO, isResultsReleasedRecently, isInternalTesting]);

  return {
    tags,
    isStoriesLoading,
    isStorifyEnabled,
    storifyWidgetId,
  };
};

export default useStorifyConfig;
